import { AxiosPromise } from 'axios';
import {
    AiPrompt,
    AiPromptEvent,
    AiPromptMetadata,
    AiSmartReplyContactPlanInfo,
    AiSmartReplyContactPlanState,
    AiSmartReplyPlanItemsPrompt,
} from '../../types/Ai';
import { libApiInstance } from '../axios';
import {
    AiActivateSmartReplyRequest,
    AiPromptStreamRequest,
    AiSmartReplyPlan,
    AiSmartReplyPlanItem,
    CallSummaryResponse,
    ChatReplyResponse,
    CreateAiPromptRequest,
    CreateAiSmartReplyPlanItemRequest,
    CreateAiSmartReplyPlanRequest,
    EditAiSmartReplyPlanRequest,
    PlanItemRequestQuery,
    RequestSmartReplySimulateWebhook,
    UpdateAiPromptRequest,
} from './types/Ai';

export function requestAiPrompt(promptId: number, signal?: AbortSignal): AxiosPromise<AiPrompt> {
    return libApiInstance.get(`/ai/prompts/${promptId}`, { signal });
}

export function requestAiPrompts(signal?: AbortSignal): AxiosPromise<AiPrompt[]> {
    return libApiInstance.get(`/ai/prompts`, { signal });
}

export function requestCreateAiPrompt(
    prompt: CreateAiPromptRequest,
    signal?: AbortSignal,
): AxiosPromise<AiPrompt> {
    return libApiInstance.put(`/ai/prompts`, prompt, { signal });
}

export function requestUpdateAiPrompt(
    prompt: UpdateAiPromptRequest,
    signal?: AbortSignal,
): AxiosPromise<AiPrompt> {
    return libApiInstance.post(`/ai/prompts/${prompt.promptId}`, prompt, {
        signal,
    });
}

export function requestDeleteAiPrompt(
    promptId: number,
    signal?: AbortSignal,
): AxiosPromise<AiPrompt> {
    return libApiInstance.delete(`/ai/prompts/${promptId}`, { signal });
}

export function requestAiPromptMetadata(
    promptId: number,
    signal?: AbortSignal,
): AxiosPromise<AiPromptMetadata> {
    return libApiInstance.get(`/ai/prompts/metadata/${promptId}`, { signal });
}

export function requestAiPromptsMetadata(signal?: AbortSignal): AxiosPromise<AiPromptMetadata[]> {
    return libApiInstance.get(`/ai/prompts/metadata`, { signal });
}

export function requestChatReply(
    request: AiPromptStreamRequest,
    signal?: AbortSignal,
): AxiosPromise<ChatReplyResponse> {
    return libApiInstance.post(`/ai/ChatReply`, request, { signal });
}

export function requestCallTranscript(
    request: AiPromptStreamRequest,
    signal?: AbortSignal,
): AxiosPromise<string> {
    return libApiInstance.post(`/ai/CallTranscript`, request, { signal });
}

export function requestRegenerateCallTranscript(
    request: AiPromptStreamRequest,
    signal?: AbortSignal,
): AxiosPromise<string> {
    return libApiInstance.post(`/ai/RegenerateCallTranscript`, request, {
        signal,
    });
}

export function requestCallSummary(
    request: AiPromptStreamRequest,
    signal?: AbortSignal,
): AxiosPromise<CallSummaryResponse> {
    return libApiInstance.post(`/ai/CallSummary`, request, { signal });
}

export function requestRegenerateCallSummary(
    request: AiPromptStreamRequest,
    signal?: AbortSignal,
): AxiosPromise<CallSummaryResponse> {
    return libApiInstance.post(`/ai/RegenerateCallSummary`, request, {
        signal,
    });
}

export function requestActivateSmartReply(
    request: AiActivateSmartReplyRequest,
    signal?: AbortSignal,
): AxiosPromise {
    return libApiInstance.post(`/ai/SmartReply/Activate`, request, {
        signal,
    });
}

export function requestSmartReplyPlan(
    planId: number,
    signal?: AbortSignal,
): AxiosPromise<AiSmartReplyPlan> {
    return libApiInstance.get(`/ai/SmartReply/Plans/${planId}`, {
        signal,
    });
}

export function requestSmartReplyPlans(signal?: AbortSignal): AxiosPromise<AiSmartReplyPlan[]> {
    return libApiInstance.get(`/ai/SmartReply/Plans`, {
        signal,
    });
}

export function requestCreateSmartReplyPlan(
    plan: CreateAiSmartReplyPlanRequest,
    signal?: AbortSignal,
): AxiosPromise<AiSmartReplyPlan> {
    return libApiInstance.put(`/ai/SmartReply/Plans/`, plan, {
        signal,
    });
}

export function requestUpdateSmartReplyPlan(
    plan: EditAiSmartReplyPlanRequest,
    signal?: AbortSignal,
): AxiosPromise<AiSmartReplyPlan> {
    return libApiInstance.post(`/ai/SmartReply/Plans/${plan.planId}`, plan, {
        signal,
    });
}

export function requestDeleteSmartReplyPlan(
    planId: number,
    signal?: AbortSignal,
): AxiosPromise<AiSmartReplyPlan> {
    return libApiInstance.delete(`/ai/SmartReply/Plans/${planId}`, {
        signal,
    });
}

export function requestSmartReplyPlanItems(
    planId: number,
    signal?: AbortSignal,
): AxiosPromise<AiSmartReplyPlanItemsPrompt[]> {
    return libApiInstance.get(`/ai/SmartReply/Plans/${planId}/Items`, {
        signal,
    });
}

export function requestCreateSmartReplyPlanItem(
    planItem: CreateAiSmartReplyPlanItemRequest,
    signal?: AbortSignal,
): AxiosPromise<AiSmartReplyPlanItem> {
    return libApiInstance.put(`/ai/SmartReply/Plans/${planItem.planId}/Items`, planItem, {
        signal,
    });
}

export function requestDeleteSmartReplyPlanItem(
    queryParams: PlanItemRequestQuery,
    signal?: AbortSignal,
): AxiosPromise<AiSmartReplyPlan> {
    return libApiInstance.delete(
        `/ai/SmartReply/Plans/${queryParams.planId}/Items/${queryParams.planItemId}`,
        {
            signal,
        },
    );
}

export function requestSmartReplyContactPlans(
    contactId: number,
    signal?: AbortSignal,
): AxiosPromise<AiSmartReplyContactPlanInfo[]> {
    return libApiInstance.get(`/ai/SmartReply/Contacts/${contactId}/Plans`, {
        signal,
    });
}

export function requestPauseSmartReplyPlan(
    contactId: number,
    contactPlanId: number,
    signal?: AbortSignal,
): AxiosPromise<boolean> {
    return libApiInstance.post(
        `/ai/SmartReply/Contacts/${contactId}/Plans/${contactPlanId}/Pause`,
        undefined,
        {
            signal,
        },
    );
}

export function requestSmartReplySimulateWebhook(
    payload: RequestSmartReplySimulateWebhook,
    signal?: AbortSignal,
): AxiosPromise<boolean> {
    return libApiInstance.post(
        `/ai/SmartReply/Contacts/${payload.contactId}/SimulateWebhook/${payload.channelIndexId}`,
        undefined,
        {
            signal,
        },
    );
}

export function requestSmartReplyContactPlanState(
    contactId: number,
    signal?: AbortSignal,
): AxiosPromise<AiSmartReplyContactPlanState[]> {
    return libApiInstance.get(`/ai/SmartReply/Contacts/${contactId}/States`, {
        signal,
    });
}

export function requestSmartReplyContactPlanEvent(
    contactPlanItemId: number,
    signal?: AbortSignal,
): AxiosPromise<AiPromptEvent> {
    return libApiInstance.get(`/ai/SmartReply/Event/${contactPlanItemId}`, {
        signal,
    });
}
